<template>
    <teleport v-if="isOpenRef" to="#root-fullscreen">
        <div
            data-app-modal-open
            :style="backdropStyle"
            class="fixed inset-x-0 z-50 flex items-end justify-center overflow-hidden md:items-center"
            @click.self="requestClose()"
            @mousedown="startDrag"
            @mousemove="moveDrag"
            @mouseup="endDrag"
            @mouseleave="endDrag"
            @touchstart="startDrag"
            @touchmove="moveDrag"
            @touchend="endDrag"
        >
            <transition name="modal-slide">
                <div
                    v-show="isOpenRef"
                    ref="modalRef"
                    :style="{ transform: `translateY(${translateY}px)` }"
                    :class="[
                        'bg-dark-base relative flex min-h-0 w-[98%] flex-col rounded-t-lg md:w-[60%]',
                        halfModal && !keyboardVisible ? 'max-h-[60%]' : 'max-h-[95%]',
                        'md:max-h-[95%]',
                        isDragging || !openAnimationDone
                            ? ''
                            : 'transition-transform duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]',
                    ]"
                >
                    <!-- Handle -->
                    <div
                        ref="handleRef"
                        class="bg-text-base absolute top-2 left-1/2 z-50 h-[5px] w-[36px] -translate-x-1/2 cursor-pointer rounded-lg"
                    >
                    </div>
                    <RawHeader
                        :back-text="backText || $t('nav.cancel')"
                        :back-action="requestClose"
                        :action-text="actionText === 'Action' ? $t('form.action') : actionText"
                        :action-action="actionAction"
                        :primary="primary"
                        :with-icons="false"
                        :add-padding="false"
                        class="sticky top-0 z-30 w-full rounded-t-xl"
                    >
                        {{ title }}
                    </RawHeader>

                    <div
                        ref="contentRef"
                        class="mb-3 min-h-0 flex-1 space-y-4 overflow-x-hidden overflow-y-auto px-2 py-1.5"
                        @focusin.capture="handleFocusIn"
                    >
                        <slot />
                    </div>
                </div>
            </transition>
        </div>
    </teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, inject } from 'vue'
import RawHeader from '@/components/RawHeader.vue'

defineOptions({
    name: 'AppModal',
})

const props = defineProps({
    actionAction: { type: Function, default: undefined },
    backText: { type: String, default: undefined },
    actionText: { type: String, default: 'Action' },
    primary: { type: Boolean, default: false },
    title: { type: String, required: true },
    isOpen: { type: Boolean, default: false },
    scrollTo: { type: String, default: '' },
    halfModal: { type: Boolean, default: false },
    disableClose: { type: Boolean, default: false },
})

const emits = defineEmits(['update:isOpen'])
const { keyboardVisible, keyboardHeight } = inject('layout-configuration', {
    keyboardVisible: ref(false),
    keyboardHeight: ref(0),
})

const isOpenRef = ref(false)
const translateY = ref(100) // pour animation d'ouverture
const backgroundOpacity = ref(0.8)
const isDragging = ref(false)
const openAnimationDone = ref(false)
const handleRef = ref(null)
const contentRef = ref(null)
const viewportTop = ref(0)
const viewportHeight = ref(0)
const viewportBaseHeight = ref(0)
let startY = 0
let scrollFocusedFieldTimer = null
const swipeThreshold = 120
const minModalViewportHeight = 320

const backdropStyle = computed(() => ({
    backgroundColor: `rgba(0,0,0,${backgroundOpacity.value})`,
    paddingBottom: keyboardVisible.value ? '0px' : 'var(--app-safe-area-bottom)',
    top: `${viewportTop.value}px`,
    height: viewportHeight.value ? `${viewportHeight.value}px` : 'var(--app-viewport-height)',
}))

const disableSelect = () => {
    document.body.style.userSelect = 'none'
    document.body.style.touchAction = 'none'
}
const enableSelect = () => {
    document.body.style.userSelect = ''
    document.body.style.touchAction = ''
}

const readViewportHeight = () =>
    window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 0

const updateViewportBounds = () => {
    const currentHeight = readViewportHeight()

    if (!keyboardVisible.value) {
        viewportBaseHeight.value = currentHeight
    }

    const baseHeight = Math.max(viewportBaseHeight.value, currentHeight)
    let availableHeight = currentHeight

    if (keyboardVisible.value && keyboardHeight.value > 0 && baseHeight - currentHeight < keyboardHeight.value * 0.5) {
        availableHeight = baseHeight - keyboardHeight.value
    }

    viewportTop.value = window.visualViewport?.offsetTop || 0
    viewportHeight.value = Math.max(Math.min(currentHeight, availableHeight), minModalViewportHeight)
}

const isFormField = (element) => ['INPUT', 'TEXTAREA', 'SELECT'].includes(element?.tagName)

const scrollFocusedFieldIntoView = () => {
    window.clearTimeout(scrollFocusedFieldTimer)

    scrollFocusedFieldTimer = window.setTimeout(
        () => {
            const activeElement = document.activeElement
            const contentElement = contentRef.value

            if (!isFormField(activeElement) || !contentElement?.contains(activeElement)) return

            const fieldRect = activeElement.getBoundingClientRect()
            const contentRect = contentElement.getBoundingClientRect()
            const fieldOffset = fieldRect.top - contentRect.top - contentRect.height / 2 + fieldRect.height / 2

            contentElement.scrollTo({
                top: contentElement.scrollTop + fieldOffset,
                behavior: 'smooth',
            })
        },
        keyboardVisible.value ? 300 : 50
    )
}

const handleFocusIn = (event) => {
    if (isFormField(event.target)) {
        scrollFocusedFieldIntoView()
    }
}

const startDrag = (e) => {
    if (handleRef.value) {
        const handleRect = handleRef.value.getBoundingClientRect()
        const clickX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX
        const clickY = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY

        const padding = 20 // 🔥 pixels de tolérance autour du handle
        const isNearHandle =
            clickX >= handleRect.left - padding &&
            clickX <= handleRect.right + padding &&
            clickY >= handleRect.top - padding &&
            clickY <= handleRect.bottom + padding

        // Si le clic/touch n'est pas dans la zone proche du handle ➔ on annule
        if (!isNearHandle) {
            return
        }
    }

    const posY = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY
    startY = posY
    isDragging.value = true
    disableSelect()
}

const moveDrag = (e) => {
    if (!isDragging.value) return
    let currentY = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY
    let distance = currentY - startY
    if (distance < 0) distance = 0
    if (distance > 500) distance = 500
    translateY.value = distance

    // 🔥 Animation du fond dynamique
    const opacity = 0.8 - distance / 800
    backgroundOpacity.value = Math.max(0.4, opacity)
}

const endDrag = () => {
    if (isDragging.value) {
        if (translateY.value > swipeThreshold) {
            requestClose()
        } else {
            translateY.value = 0
            backgroundOpacity.value = 0.8
        }
    }
    isDragging.value = false
    enableSelect()
}

const requestClose = () => {
    if (props.disableClose) return
    setOpen(false)
}

const setOpen = (state) => {
    isOpenRef.value = state
    emits('update:isOpen', state)

    if (state) {
        // ➔ Animation d'ouverture
        translateY.value = 100
        backgroundOpacity.value = 0.8
        openAnimationDone.value = false
        updateViewportBounds()

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                translateY.value = 0
                openAnimationDone.value = true
            })
        })
    } else {
        translateY.value = 0
        backgroundOpacity.value = 0.8
        isDragging.value = false
        openAnimationDone.value = false
        enableSelect()
    }
}

watch(
    () => props.isOpen,
    (newVal) => {
        setOpen(newVal)
    }
)

watch([keyboardVisible, keyboardHeight], () => {
    updateViewportBounds()
    scrollFocusedFieldIntoView()
})

onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
    window.addEventListener('resize', updateViewportBounds)
    window.visualViewport?.addEventListener('resize', updateViewportBounds)
    window.visualViewport?.addEventListener('scroll', updateViewportBounds)
    updateViewportBounds()
    setOpen(props.isOpen)
})
onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown)
    window.removeEventListener('resize', updateViewportBounds)
    window.visualViewport?.removeEventListener('resize', updateViewportBounds)
    window.visualViewport?.removeEventListener('scroll', updateViewportBounds)
    window.clearTimeout(scrollFocusedFieldTimer)
})

const handleKeydown = (e) => {
    if (e.key === 'Escape' && isOpenRef.value) {
        requestClose()
    }
}
</script>

<style scoped>
.modal-slide-enter-active {
    transition: all 300ms cubic-bezier(0.22, 1, 0.36, 1);
}
.modal-slide-leave-active {
    transition: all 200ms cubic-bezier(0.22, 1, 0.36, 1);
}
.modal-slide-enter-from {
    transform: translateY(100%);
    opacity: 0;
}
.modal-slide-enter-to {
    transform: translateY(0);
    opacity: 1;
}
.modal-slide-leave-from {
    transform: translateY(0);
    opacity: 1;
}
.modal-slide-leave-to {
    transform: translateY(100%);
    opacity: 0;
}
</style>
